import React, { useState, useCallback, useEffect } from "react";
import EditableTaskPrompt from "./EditableTaskPrompt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faInfoCircle,
  faPlus,
  faSave,
  faTrash,
  faUpload,
  IconLookup,
} from "@fortawesome/free-solid-svg-icons";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import toast from "@/utils/toast";

import {
  EditableTaskProps,
  TaskPromptProps,
  PromptOptions,
  PromptType,
  TaskFormData,
} from "@/components/tasks/types";
import { apiClient } from "@/api";
import { useNavigate } from "react-router";
import { Task, TaskDependency, TaskPrompt } from "@/api/types";
import AppButton from "@/components/structure/AppButton";
import RichTextEditor from "@/components/widgets/richtext/RichTextEditor";
import AssignmentsSection from "@/components/widgets/AssignmentsSection";
import TaskList from "./TaskList";
import IconChooser from "@/components/widgets/IconChooser";
import {
  FormProvider,
  useForm,
  FieldValues,
  Controller,
} from "react-hook-form";

export default function EditableTask({
  id,
  task,
  project,
  onDirtyChange = (_dirty: boolean) => {},
  showTaskSummary = false,
}: EditableTaskProps) {
  const navigate = useNavigate();

  // ==========================================================================
  // === form defns: first one for the main task info, second one for the prompts
  // ==========================================================================

  // first form, for use by the main task details
  const {
    register: taskFormRegister,
    handleSubmit: handleTaskFormSubmit,
    control: taskFormControl,
    watch: taskFormWatch,
    formState: { errors: taskFormErrors, isDirty: isTaskFormDirty },
  } = useForm({
    defaultValues: {
      title: task?.title || "",
      description: task?.description || "",
      icon: task?.icon || "",
      instructions: task?.instructions || "",
      isReviewable: task?.isReviewable ?? true,
      isDiscussable: task?.isDiscussable ?? true,
      allowCrossProjectDeps: task?.allowCrossProjectDeps ?? true,
      dependencyConfig: task?.dependencies || [],
      participants: task?.participants || [],
      participantCohorts: task?.participantCohorts || [],
      facilitators: task?.facilitators || [],
      facilitatorCohorts: task?.facilitatorCohorts || [],
    },
  });

  // second form, for use by prompts' Controller instances
  const methods = useForm<TaskFormData>({
    defaultValues: {},
    mode: "onChange",
  });
  const {
    handleSubmit,
    formState: { isDirty: isTaskPromptsFormDirty },
  } = methods;

  // track dirty state and notify parent so they can stop navigation if needed
  useEffect(() => {
    if (isTaskFormDirty || isTaskPromptsFormDirty) {
      onDirtyChange(true);
    }
  }, [isTaskFormDirty, isTaskPromptsFormDirty, onDirtyChange]);


  // ==========================================================================
  // === prompt manipulation
  // ==========================================================================

  // the client-side state of prompts, before submission
  const [localPrompts, setLocalPrompts] = useState<TaskPrompt[]>(
    task?.prompts || []
  );


  // -----------------------------------------------
  // --- track which prompts are expanded/collapsed
  // -----------------------------------------------

  const [expandedPrompts, setExpandedPrompts] = useState<{[key:string]: boolean}>(
    () => {
      const initialState: {[key:string]: boolean} = {};
      (task?.prompts || []).forEach(prompt => {
        if (prompt.uuid) {
          initialState[prompt.uuid] = true;
        }
      });
      return initialState;
    }
  );

  const setPromptExpanded = useCallback((uuid: string, expanded: boolean) => {
    setExpandedPrompts(prev => ({
      ...prev,
      [uuid]: expanded
    }));
  }, []);

  const isPromptExpanded = useCallback((uuid?: string) => {
    if (!uuid) return true;
    return expandedPrompts[uuid] === undefined || expandedPrompts[uuid]
  }, [expandedPrompts]);


  // -----------------------------------------------
  // --- update prompts order, contents, etc.
  // -----------------------------------------------

  const handlePromptChange = useCallback(
    (index: number, field: keyof TaskPrompt, value: string | PromptOptions) => {
      // perform field update
      // (to rewrite the indices, set index: i+1 for all prompts, but we'll skip this for now)
      setLocalPrompts((prev) => prev.map((prompt, i) =>
        i === index ? { ...prompt, [field]: value } : prompt
      ));
      onDirtyChange(true);
    },
    [localPrompts]
  );

  const reorderPrompts = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex < 0 || fromIndex >= localPrompts.length) return;
      if (toIndex < 0 || toIndex >= localPrompts.length) return;

      // shuffle moved prompt to new index, rewrite prompt indices to be sequential
      setLocalPrompts((prev) => {
        const newPrompts = [...prev];
        const [movedPrompt] = newPrompts.splice(fromIndex, 1);
        newPrompts.splice(toIndex, 0, movedPrompt);

        // reset their indices
        newPrompts.forEach((p, i) => (p.index = i+1));

        return newPrompts;
      });
      onDirtyChange(true);
    },
    [localPrompts]
  );

  // apparently there's some kind of race condition if we set both type and options at the same time
  // so we introduce this method to handle both at once
  const handlePromptTypeAndOptionsChange = useCallback(
    (index: number, newType: PromptType, newOptions: PromptOptions) => {
      setLocalPrompts((prev) => prev.map((prompt, i) =>
        i === index ? { ...prompt, type: newType, options: newOptions } : prompt
      ));
      onDirtyChange(true);
    },
    [localPrompts]
  );

  const addPrompt = useCallback(() => {
    // append new prompt to end of current prompt list
    setLocalPrompts((prev) => [...prev, {
      uuid: crypto.randomUUID(),
      index: prev.length + 1,
      name: "",
      description: "",
      type: "single-line-textbox",
      options: { "max-characters": 0, validation: {} },
    }]);
    onDirtyChange(true);
  }, [localPrompts]);

  const removePrompt = useCallback(
    (index: number) => {
      // remove prompt at index, rewrite indices to be sequential
      setLocalPrompts((prev) =>
        prev
          .filter((_, i) => i !== index)
          .map((p, i) => ({ ...p, index: i+1 }))
      );
      onDirtyChange(true);
    },
    [localPrompts]
  );

  // ==========================================================================
  // === task creating, saving
  // ==========================================================================

  const saveTask = async (id: string, data: Task) => {
    try {
      const payload = {
        ...data,
        prompts: localPrompts
      };

      const response = await apiClient.put(`/tasks/${id}/`, payload);

      console.log("Task saved successfully: ", response);
      toast.info("Task saved successfully");
      onDirtyChange(false);
    } catch (error) {
      console.error("Error saving task:", error);
      toast.apiError("Failed to save task", error);
    }
  };

  const createTask = async (data: Task) => {
    try {
      const payload = {
        ...data,
        // remove the ID from the prompts, since the backend will assign new ones
        prompts: localPrompts.map(({ id, ...rest }) => rest),
        project: project?.id
      };

      const response = await apiClient.post<Task>("/tasks/", payload);

      console.log("Task created successfully: ", response);
      toast.info("Task created successfully");
      onDirtyChange(false);

      // navigate to the new task page so that we can edit it
      navigate(`/tasks/${response.id}`);
    } catch (error) {
      console.error("Error creating task:", error);
      toast.apiError("Error creating task", error);
    }
  };

  const taskFormSubmitted = (data: FieldValues) => {
    if (id) {
      saveTask(id, data as Task);
    } else {
      createTask(data as Task);
    }
  };

  let persistButton = null;
  if (id) {
    persistButton = (
      <AppButton variant="success" onClick={handleTaskFormSubmit(taskFormSubmitted)}>
        <FontAwesomeIcon icon={faUpload} className="mr-2" />
        Save Task
      </AppButton>
    );
  } else if (project) {
    persistButton = (
      <AppButton variant="success" onClick={handleTaskFormSubmit(taskFormSubmitted)}>
        <FontAwesomeIcon icon={faUpload} className="mr-2" />
        Create Task
      </AppButton>
    );
  }


  // ==========================================================================
  // === component rendering
  // ==========================================================================

  const taskSummary = (
    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-blue-800 font-medium">
          <FontAwesomeIcon
            className="text-blue-500 text-lg"
            icon={faInfoCircle}
          />
          <span>Task Summary</span>
        </div>
      </div>
      <p className="text-gray-600 text-sm mt-2 mb-0">
        This task contains {localPrompts.length} prompt
        {localPrompts.length !== 1 ? "s" : ""} to complete.
      </p>
    </div>
  );

  return (
    <div className="space-y-6">
      <form onSubmit={handleTaskFormSubmit(taskFormSubmitted)}>
        {/* Editable Task Header */}
        <div className="bg-gray-100 p-4 rounded-lg space-y-4">
          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
            Task Configuration
          </h4>

          {/* Title Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Title
            </label>
            <input
              type="text"
              {...taskFormRegister("title", { required: true })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter task title..."
            />
            {taskFormErrors.title && (
              <p className="text-red-500 text-sm mt-1 ml-1">Task title is required</p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Description
            </label>

            <p className="text-gray-500 text-sm mb-1">
              A brief description of the task for display in task lists.
            </p>

            <textarea
              {...taskFormRegister("description", { required: false })}
              rows={3}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
              placeholder="Enter task description..."
            />
          </div>

          {/* Icon Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Icon
            </label>
            <div className="max-w-xs">
              <Controller
                control={taskFormControl}
                name="icon"
                render={({ field: { onChange, value } }) => (
                  <>
                    <IconChooser
                      value={value}
                      onChange={onChange}
                      placeholder="Search icons..."
                    />
                    {value && (
                      <div>
                        <p className="text-sm text-gray-500 mt-2">
                          Selected Icon:{" "}
                          <FontAwesomeIcon
                            icon={
                              {
                                prefix: "fas",
                                iconName: value || "question",
                              } as IconLookup
                            }
                          />{" "}
                          ({value})
                        </p>
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          {/* Instructions Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Instructions
            </label>
            <p className="text-gray-500 text-sm mb-1">
              Detailed instructions that are displayed to users when they work
              on this task. Supports rich text formatting.
            </p>
            <div className="space-y-2">
              <Controller
                control={taskFormControl}
                name="instructions"
                render={({ field: { onChange, value } }) => (
                  <RichTextEditor
                    initialValue={value}
                    ownerId={id}
                    rows={8}
                    ownerType="task"
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>

          {/* Extra Options: Reviewable, Discussible */}
          <div>
            <h3>Task Review, Discussion</h3>
            <div className="pl-4 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <input type="checkbox" {...taskFormRegister("isReviewable")} className="mr-2" />
                Is this task reviewable?
              </label>

              <label className="block text-sm font-medium text-gray-700 mb-1">
                <input type="checkbox" {...taskFormRegister("isDiscussable")} className="mr-2" />
                Is this task discussible?
              </label>

              <label className="block text-sm font-medium text-gray-700 mb-1">
                <input type="checkbox" {...taskFormRegister("allowCrossProjectDeps")} className="mr-2" />
                Allow dependencies on tasks from other projects?
              </label>
            </div>
          </div>

          {/* Dependent Tasks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dependent Tasks
            </label>
            <p className="text-gray-500 text-sm mb-1">
              Tasks that must be completed before this task can be started.
            </p>
            <Controller
              control={taskFormControl}
              name="dependencyConfig"
              render={({ field: { onChange, value } }) => (
                <TaskList
                  currentTaskId={id}
                  currentProjectId={project?.id}
                  selectedTasks={value}
                  setSelectedTasks={onChange}
                  allowCrossProject={taskFormWatch("allowCrossProjectDeps")}
                />
              )}
            />
          </div>

          <AssignmentsSection
            control={taskFormControl}
            defaultExpanded={false}
            sectionId="task-assignments-section"
          />
        </div>
      </form>

      {/* Task Summary */}
      {showTaskSummary && taskSummary}

      {/* Toggle all prompts open/closed */}
      <div className="space-y-2 text-right">
        
        {localPrompts.length > 1 && (
          <button
            type="button"
            onClick={() => {
              const allExpanded = localPrompts.every(prompt => isPromptExpanded(prompt.uuid!));
              const newState: {[key:string]: boolean} = {};
              localPrompts.forEach(prompt => {
                if (prompt.uuid) {
                  newState[prompt.uuid] = !allExpanded;
                }
              });
              setExpandedPrompts(newState);
            }}
            className="text-blue-600 hover:text-blue-800 text-sm mb-2 cursor-pointer"
          >
            {localPrompts.every(prompt => isPromptExpanded(prompt.uuid!)) ? <><FontAwesomeIcon icon="caret-down" /> Collapse All Prompts</> : <><FontAwesomeIcon icon="caret-right" /> Expand All Prompts</> }
          </button>
        )}
      </div>

      {/* Task Prompts */}
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit((data) => {
            console.log("Submitted data: ", data);
          })}
          className="space-y-6"
        >
          {localPrompts.length === 0 ? (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <p className="text-gray-500 text-lg">No prompts to display</p>
              <p className="text-gray-400 text-sm mt-2 mb-4">
                Add some prompts to see them here
              </p>
              <button
                onClick={addPrompt}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 mx-auto"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Add First Prompt</span>
              </button>
            </div>
          ) : (
            <motion.div layout className="space-y-4">
              <AnimatePresence mode="popLayout">
                {localPrompts.map((prompt, index) => (
                  <motion.div
                    layout
                    key={`prompt-${prompt.uuid}`}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                  >
                    {/* Prompt number indicator */}
                    <div className="flex items-start space-x-4">
                      <div className="flex flex-col flex-shrink-0 w-10 items-center rounded-full bg-gray-100 p-4 px-8">
                        <div
                          className={`flex-shrink-0 w-8 h-8 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium cursor-pointer ${ isPromptExpanded(prompt?.uuid) ? 'bg-blue-100' : 'bg-gray-300' }`}
                          title={`Prompt ${index + 1}`}
                          onClick={() => {
                            setPromptExpanded(prompt.uuid!, !isPromptExpanded(prompt.uuid!));
                          }}
                        >
                          {index + 1}
                        </div>

                        {/* Button to move this prompt up */}
                        <button
                          onClick={() => {
                            reorderPrompts(index, index - 1);
                          }}
                          className={`flex-shrink-0 px-2 focus:outline mt-2 ${
                            index === 0
                              ? "text-gray-300 cursor-not-allowed"
                              : "cursor-pointer text-gray-600 hover:text-gray-800"
                          }`}
                          title="Move prompt up"
                          disabled={index === 0}
                        >
                          <FontAwesomeIcon icon={faArrowUp} />
                        </button>

                        {/* Button to move this prompt down */}
                        <button
                          onClick={() => {
                            reorderPrompts(index, index + 1);
                          }}
                          className={`flex-shrink-0 px-2 focus:outline mt-2 ${
                            index === localPrompts.length - 1
                              ? "text-gray-300 cursor-not-allowed"
                              : "cursor-pointer text-gray-600 hover:text-gray-800"
                          }`}
                          title="Move prompt up"
                          disabled={index === localPrompts.length - 1}
                        >
                          <FontAwesomeIcon icon={faArrowDown} />
                        </button>

                        {/* Delete button */}
                        <button
                          onClick={() => removePrompt(index)}
                          className="flex-shrink-0 px-2 text-red-600 hover:text-red-800 focus:outline mt-2 cursor-pointer"
                          title="Delete prompt"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>

                      <div className="flex-1">
                        <EditableTaskPrompt
                          expanded={isPromptExpanded(prompt.uuid!)}
                          setExpanded={(expanded) => setPromptExpanded(prompt.uuid!, expanded)}
                          prompt={prompt}
                          onNameChange={(name) =>
                            handlePromptChange(index, "name", name)
                          }
                          onDescriptionChange={(description) =>
                            handlePromptChange(
                              index,
                              "description",
                              description
                            )
                          }
                          onReviewableChange={(isReviewable) =>
                            handlePromptChange(
                              index,
                              "isReviewable",
                              isReviewable
                            )
                          }
                          onHeaderChange={(header) =>
                            handlePromptChange(index, "header", header)
                          }
                          onTypeChange={(type) =>
                            handlePromptChange(index, "type", type)
                          }
                          onOptionsChange={(options) =>
                            handlePromptChange(index, "options", options)
                          }
                          onTypeAndOptionsChange={(type, options) =>
                            handlePromptTypeAndOptionsChange(
                              index,
                              type,
                              options
                            )
                          }
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </form>
      </FormProvider>

      {/* add prompt, save task buttons */}

      <div className="flex justify-end items-center mt-4">
        {localPrompts.length > 0 && (
          <AppButton variant="primary" onClick={addPrompt} className="mr-2">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            <span>Add Prompt</span>
          </AppButton>
        )}
        {persistButton}
      </div>
    </div>
  );
}
